<template>
    <div class="lms__superadmin_users_bulk_add_upload">
        <div class="lms__superadmin_users_bulk_add_upload_main">
            <component-title
                :class="'lms__superadmin_users_bulk_add_upload_main_title'"
                :size="'large'"
                :underline="false"
            >Bulk invite users
            </component-title>
            <component-text>Bulk invite enables you to invite multiple learners to your workspace and assign them to the right Organisation unit and any required groups.</component-text>
            <template v-if="user.isSuperadmin() && !loggedInUserTokenWorkspaceId">
                <form-select
                    :prevent-clear="true"
                    :name="'user-customer'"
                    :disabled="hasBlockedCustomer"
                    :label="'Customer'"
                    :is-type="'select'"
                    :required="true"
                    :value="selectedCustomerId"
                    :v-validate="'required'"
                    :data-vv-as="'customer'"
                    :get-pagination-getter="'getLmsSuperadminCustomersPagination'"
                    :get-items-getter="'getLmsSuperadminCustomersMappedIdNameStatus'"
                    :get-items-getter-mapped="'getLmsSuperadminCustomersMappedLabelValue'"
                    :get-items-dispatch="'lmsSuperadminCustomersGet'"
                    :item-name="['customer', 'customers']"
                    :search-queryable="true"
                    :table-labels="[
                        {
                            key: 'name',
                            title: 'Name',
                            type: 'string',
                            sortable: true,
                            queryable: true
                        }
                    ]"
                    @input="updateCustomer"
                    @input-table="updateCustomer"
                    @request-selected="setWorkspaces"
                />
            </template>
            <template v-else>
                <form-text
                    :label="'Customer'"
                    :name="'lms-superadmin-users-bulk-add-upload-customer-readonly'"
                    :read-only="true"
                    :value="selectedCustomerName"
                ></form-text>
            </template>
            <template v-if="!loggedInUserTokenWorkspaceId">
                <form-select
                    :prevent-clear="true"
                    :disabled="hasBlockedWorkspace || !selectedCustomerId"
                    :name="'lms-superadmin-user-bulk-add-upload-workspace'"
                    :label="'Workspace'"
                    :get-items-dispatch="!user.isSuperadmin() && user.isMasterAdministrator() && selectedCustomerId ? 'lmsSuperadminWorkspacesGet' : ''"
                    :get-items-getter="'getLmsSuperadminWorkspacesMappedIdNameStatus'"
                    :get-items-getter-mapped="'getLmsSuperadminWorkspacesMappedLabelValue'"
                    :item-name="['workspace', 'workspaces']"
                    :is-type="'select'"
                    :required="true"
                    :table-labels="[
                        {
                            key: 'name',
                            title: 'Name',
                            type: 'string',
                            sortable: true,
                            queryable: true
                        },
                        {
                            key: 'customer',
                            title: 'Customer',
                            type: 'string'
                        }
                    ]"
                    :value="selectedWorkspaceId"
                    :v-validate="'required'"
                    :data-vv-as="'workspace'"
                    @input="updateWorkspace"
                    @input-table="updateWorkspace"
                />
            </template>
            <template v-else>
                <form-text
                    :label="'Workspace'"
                    :name="'lms-superadmin-users-bulk-add-upload-workspace-readonly'"
                    :read-only="true"
                    :value="selectedWorkspaceName"
                ></form-text>
            </template>
            <component-text>Download the XLSX template below and complete it with the following information for each learner:</component-text>
            <component-wrapper :class="'lms__superadmin_users_bulk_add_upload_main_samples'">
                <component-labelsample
                    :label="'First name'"
                    :required="true"
                    :sample="'e.g. - John'"
                />
                <component-labelsample
                    :label="'Last name'"
                    :required="true"
                    :sample="'e.g. - Johnson'"
                />
                <component-labelsample
                    :label="'Email address'"
                    :required="true"
                    :sample="'e.g. - john.johnson@melearning.co.uk'"
                />
                <component-labelsample
                    :label="'Organisation unit'"
                    :required="true"
                    :sample="'Select from the dropdown list.'"
                />
                <component-text
                    :class="'lms__superadmin_users_bulk_add_upload_main_samples_text'"
                >You can also choose to complete any non-mandatory fields as required such as:
                </component-text>
                <component-labelsample
                    :label="'Groups'"
                    :required="false"
                    :sample="'e.g. - Management, Admin (must be valid with group names in selected workspace)'"
                />
            </component-wrapper>
            <component-text>In the next step, you will be able to preview your upload and assign any other system role to learners if needed</component-text>
            <component-wrapper :class="'lms__superadmin_users_bulk_add_upload_main_actions'">
                <div class="columns is-block-mobile columns--margin-1-mobile">
                    <div class="column is-narrow">
                        <component-button
                            :disabled="!selectedWorkspaceId || requestGetWorkspaceCustomFieldsPending"
                            :name="'lms-superadmin-users-bulk-add-upload-xls-download'"
                            :icon="true"
                            :icon-align="'right'"
                            :icon-name="'download'"
                            :theme="'default'"
                            @click="xlsDownload"
                        >Download template
                        </component-button>
                    </div>
                    <div class="column is-narrow">
                        <component-button
                            :name="'lms-superadmin-users-bulk-add-upload-xls-upload'"
                            :icon="true"
                            :icon-align="'right'"
                            :icon-name="'upload'"
                            :theme="'primary'"
                            :hint="uploadedFile ? uploadedFile : ''"
                            @click="xlsUpload"
                        >{{ uploadedFile ? 'Change XLS' : 'Upload XLS' }}
                        </component-button>
                    </div>
                    <div class="column is-narrow">
                        <component-button
                            :disabled="!uploadedFile || !selectedCustomerId || !selectedWorkspaceId || requestGetOrgUnitsPending || requestGetGroupsPending"
                            :name="'lms-superadmin-users-bulk-add-upload-next-step'"
                            :theme="'primary'"
                            :to="{ name: 'lms-superadmin-user-bulk-add-preview' }"
                        >Next step
                        </component-button>
                    </div>
                </div>
            </component-wrapper>
        </div>
        <a class="lms__superadmin_users_bulk_add_upload_xls_download hidden" id="xls-file-download"></a>
        <component-system-uploader-xls ref="xlsuploader"/>
    </div>
</template>

<script>
import ComponentText from '@/components/default/shared/ComponentText'
import ComponentButton from '@/components/default/shared/ComponentButton'
import ComponentLabelsample from '@/components/default/shared/ComponentLabelSample'
import ComponentWrapper from '@/components/default/shared/ComponentWrapper'
import ComponentSystemUploaderXls from '@/components/default/shared/ComponentSystemUploaderXLS'
import FormSelect from '@/components/default/forms/FormSelect'
import { User } from '@/User'
import { watchContentChangeEmit } from '@/helpers/mixins'
import ComponentNote from '@/components/default/shared/ComponentNote'
import ComponentTitle from '@/components/default/shared/ComponentTitle'
import FormText from '@/components/default/forms/FormText'

export default {
  name: 'lms-superadmin-users-bulk-add-upload',
  components: { FormText, ComponentTitle, ComponentNote, FormSelect, ComponentSystemUploaderXls, ComponentWrapper, ComponentLabelsample, ComponentButton, ComponentText },
  mixins: [watchContentChangeEmit],
  data () {
    return {
      user: new User(),
      rolesRequestPending: false,
      requestGetWorkspaceCustomFieldsPending: false,
      requestGetOrgUnitsPending: false,
      requestGetGroupsPending: false
    }
  },
  computed: {
    loggedInUserTokenCustomerId () {
      return this.$store.getters.getAuthUserSecondStageTokenDataCustomerId
    },
    loggedInUserTokenCustomerName () {
      return this.$store.getters.getAuthUserSecondStageTokenDataCustomerName
    },
    loggedInUserTokenWorkspaceId () {
      return this.$store.getters.getAuthUserSecondStageTokenDataWorkspaceId
    },
    loggedInUserTokenWorkspaceName () {
      return this.$store.getters.getAuthUserSecondStageTokenDataWorkspaceName
    },
    loggedInUserProfilesWithMAorUARoles () {
      return this.$store.getters.getLmsSuperadminUserProfilesWithUAOrMARole
    },
    loggedInUserProfilesCustomersWithMAorUARoles () {
      return this.$store.getters.getLmsSuperadminUserProfilesCustomersWithUAOrMARole
    },
    routeQueryWorkspaceId () {
      return this.$route.query.workspaceId
    },
    uploadedFile () {
      return this.$store.getters.getLmsSuperadminUsersBulkFile
    },
    activeWorkspaceName () {
      return this.$store.getters.getAuthUserSecondStageTokenDataWorkspaceName
    },
    hasBlockedWorkspace () {
      return this.$store.getters.getLmsSuperadminUsersBulkCustomBlockWorkspace
    },
    hasBlockedCustomer () {
      return this.$store.getters.getLmsSuperadminUsersBulkCustomBlockCustomer
    },
    activeWorkspaceId () {
      return this.$store.getters.getAuthUserSecondStageTokenDataWorkspaceId
    },
    rawWorkspaces () {
      return this.$store.getters.getLmsSuperadminWorkspaces
    },
    selectedWorkspaceId () {
      return this.$store.getters.getLmsSuperadminUsersBulkWorkspaceId
    },
    selectedWorkspaceName () {
      return this.$store.getters.getLmsSuperadminUsersBulkWorkspaceName
    },
    selectedCustomerId () {
      return this.$store.getters.getLmsSuperadminUsersBulkCustomerId
    },
    selectedCustomerName () {
      return this.$store.getters.getLmsSuperadminUsersBulkCustomerName
    },
    watchContent () {
      return this.uploadedFile + this.selectedWorkspaceId
    },
    workspaceGroups () {
      return this.$store.getters.getLmsSuperadminGroups
    },
    workspaceOrgUnits () {
      return this.$store.getters.getLmsSuperadminOrgUnitsMappedIdName
    }
  },
  watch: {
    selectedCustomerId (value) {
      if (value) {
        this.getCustomerRoles()
      }
    },
    selectedWorkspaceId (value) {
      if (value) {
        this.handleGetWorkspaceContent()
      }
    }
  },
  methods: {
    clearWorkspace (data) {
      this.clearOrgUnit()
      this.$store.dispatch('lmsSuperadminUsersBulkSetWorkspaceId', '')
      this.$store.dispatch('lmsSuperadminUsersBulkSetWorkspaceName', '')
    },
    clearOrgUnit (data) {
      this.$store.dispatch('lmsSuperadminUsersBulkSetOrgUnitId', '')
      this.$store.dispatch('lmsSuperadminUsersBulkSetOrgUnitName', '')
    },
    getCustomerRoles () {
      this.$store.dispatch('lmsSuperadminCustomerRolesGet', this.selectedCustomerId)
    },

    handleGetWorkspaceContent () {
      return new Promise((resolve) => {
        let requestsCounter = 0
        let requestsArr = [
          this.getWorkspaceCustomFields,
          this.getWorkspaceGroups,
          this.getWorkspaceOrgUnits
        ]

        for (let fn of requestsArr) {
          fn().finally(() => {
            requestsCounter++
            if (Number(requestsArr.length) === Number(requestsCounter)) {
              resolve()
            }
          })
        }
      })
    },
    getWorkspaceCustomFields () {
      this.requestGetWorkspaceCustomFieldsPending = true
      return this.$store.dispatch('lmsSuperadminCustomFieldsGet', this.selectedWorkspaceId).finally(() => {
        this.requestGetWorkspaceCustomFieldsPending = false
      })
    },
    getWorkspaceGroups (data) {
      this.requestGetGroupsPending = true
      return this.$store.dispatch('lmsSuperadminGroupsGet', data || this.selectedWorkspaceId).then(response => {
        const mappedGroups = this.$store.getters.getLmsSuperadminGroupsMappedIdNameSuggestedCourses
        this.$store.dispatch('lmsSuperadminUsersBulkSetCustomGroupsSuggestedCourses', mappedGroups)
      }).finally(() => {
        this.requestGetGroupsPending = false
      })
    },
    getWorkspaceOrgUnits (data) {
      this.requestGetOrgUnitsPending = true
      return this.$store.dispatch('lmsSuperadminOrgUnitsGet', { requestData: this.selectedWorkspaceId, preventAddUnit: true }).then(response => {
        const mappedOrgUnits = this.$store.getters.getLmsSuperadminOrgUnitsMappedIdNameSuggestedCourses
        this.$store.dispatch('lmsSuperadminUsersBulkSetCustomOrgUnitsSuggestedCourses', mappedOrgUnits)
      }).finally(() => {
        this.requestGetOrgUnitsPending = false
      })
    },
    setWorkspaces (data) {
      if (data && data.extraData) {
        this.$store.dispatch('lmsSuperadminWorkspacesSet', { data: data.extraData })
      }
    },
    updateWorkspace (data) {
      this.$store.dispatch('lmsSuperadminUsersBulkSetWorkspaceId', data.value)
      if (data.rawLabel || data.customLabel || data.label) {
        this.$store.dispatch('lmsSuperadminUsersBulkSetWorkspaceName', data.rawLabel || data.customLabel || data.label)
      }
    },
    clearWorkspace (data) {
      this.$store.dispatch('lmsSuperadminUsersBulkSetWorkspaceId', '')
      this.$store.dispatch('lmsSuperadminUsersBulkSetWorkspaceName', '')
    },
    updateCustomer (data) {
      this.clearWorkspace()
      this.$store.dispatch('lmsSuperadminUsersBulkSetCustomerId', data.value)
      if (data.rawLabel || data.customLabel || data.label) {
        this.$store.dispatch('lmsSuperadminUsersBulkSetCustomerName', data.rawLabel || data.customLabel || data.label)
      }
      this.setWorkspaces(data)
    },
    xlsDownload () {
      this.$store.dispatch('lmsSuperadminUsersBulkXLSDownload', { workspaceId: this.selectedWorkspaceId, workspaceName: this.selectedWorkspaceName })
    },
    xlsUpload () {
      this.$refs.xlsuploader.openFileUploader()
    }
  },
  mounted () {
    if (this.loggedInUserTokenCustomerId) {
      this.updateCustomer({ value: this.loggedInUserTokenCustomerId, label: this.loggedInUserTokenCustomerName })
    }
    if (this.loggedInUserTokenWorkspaceId) {
      this.updateWorkspace({ value: this.loggedInUserTokenWorkspaceId, label: this.loggedInUserTokenWorkspaceName })
    }
  }
}
</script>

<style lang="scss">
    @import "~@/assets/scss/components/lms/superadmin/users/_lmssuperadminusersbulkaddupload.scss";
</style>
